import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import {
  Crop as CropIcon,
  Eye,
  FileText,
  Loader2,
  Palette,
  Pencil,
  Plus,
  Star,
  Tags,
  Trash2,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { ConfirmationDialog } from "@/components/shared/ConfirmationDialog";
import { FileUpload } from "@/components/shared/FileUpload";
import {
  TableActionsMenu,
  type TableAction,
} from "@/components/shared/TableActionsMenu";
import { BrandLogoPreview } from "@/components/features/brands/BrandLogoPreview";
import { BrandColorDialog } from "@/components/features/brands/BrandColorDialog";
import { AdendaField } from "@/components/features/adenda/AdendaField";
import { InvoiceBrandColorSection } from "@/components/features/company-image/InvoiceBrandColorSection";
import { InvoiceLogoCropDialog } from "@/components/features/company-image/InvoiceLogoCropDialog";
import { JPG_PNG_ACCEPT } from "@/components/features/company-image/invoiceLogoPreview";
import { brandKeys } from "@/lib/queryKeys";
import { apiClient } from "@/services/apiClient";
import type { InvoiceLogoCropPayload } from "@/services/authService";
import {
  createBrand,
  deleteBrand,
  listBrands,
  updateBrand,
  uploadBrandLogo,
  type Brand,
  type UpdateBrandPayload,
} from "@/services/brandService";

const PREVIEW_FRAME_PX = 44;
/** Stands in for the legal-entity row, which has no brand id of its own. */
const LEGAL_ENTITY_ROW_ID = "__legal_entity__";

/** The crop dialog speaks the tenant's field names; the brand API speaks the
 *  column names. One adapter here beats forking the dialog. */
function toCropDialogPayload(brand: Brand): InvoiceLogoCropPayload {
  return {
    invoiceLogoCropX: brand.logoCropX,
    invoiceLogoCropY: brand.logoCropY,
    invoiceLogoCropW: brand.logoCropW,
    invoiceLogoCropH: brand.logoCropH,
    invoiceLogoSizePt: brand.logoSizePt,
  };
}

function fromCropDialogPayload(p: InvoiceLogoCropPayload): UpdateBrandPayload {
  return {
    logoCropX: p.invoiceLogoCropX,
    logoCropY: p.invoiceLogoCropY,
    logoCropW: p.invoiceLogoCropW,
    logoCropH: p.invoiceLogoCropH,
    logoSizePt: p.invoiceLogoSizePt,
  };
}

const WHOLE_IMAGE_CROP: InvoiceLogoCropPayload = {
  invoiceLogoCropX: 0,
  invoiceLogoCropY: 0,
  invoiceLogoCropW: 1,
  invoiceLogoCropH: 1,
  invoiceLogoSizePt: 76,
};

export interface BrandsCardProps {
  /** The tenant's effective invoice logo (invoiceLogoImage ?? profileImage).
   *  A brand with no logo of its own falls back to it. */
  tenantInvoiceLogoUrl: string | null;
  /**
   * The company's standing adenda, shown verbatim inside the marca editor
   * while the marca inherits it. Without it the inherit state is a ticked box
   * and nothing else — the user cannot see what the marca will print, and
   * nothing hints that untilding reveals a field.
   */
  companyAdendaText: string | null;
  /**
   * The legal entity's own invoice identity, rendered as the FIRST row of the
   * list and edited in a dialog. It is the identity an invoice carries when no
   * marca applies — the same kind of thing as a marca, so it belongs in the
   * same list rather than in a separate card that happens to look different.
   *
   * Omitted, the row is hidden and the list shows marcas only.
   */
  legalEntity?: {
    /** Razón social, or the commercial name when there is none. */
    name: string;
    /** Opens the invoice-identity editor. The dialog itself lives in the page,
     *  because the same editor is reachable from the company-logo card — one
     *  dialog, two entry points, rather than two copies of it. */
    onEdit: () => void;
    /** True when the row has a logo of its own rather than the company's. */
    hasOwnLogo: boolean;
    /** Drops back to the company logo. Same action every marca row has — with
     *  the toggle gone from the editor, this is how you undo a dedicated
     *  image. */
    onUseCompanyLogo: () => void;
    /** Invoice brand colour, edited through the same dialog the marcas use. */
    brandColor: string | null;
    onSaveColor: (brandColor: string | null) => void;
    colorSaving: boolean;
  };
}

/**
 * Marcas — the commercial identities this legal entity invoices under.
 *
 * Sits beside CompanyImageCard rather than inside it: that card configures the
 * LEGAL ENTITY's own branding, which stays the default identity and the
 * fallback for every brand. Merging the two would suggest a brand replaces the
 * company, which is exactly what it must not do — the CFE keeps the razón
 * social and RUT no matter which brand is chosen.
 */
export function BrandsCard({
  tenantInvoiceLogoUrl,
  companyAdendaText,
  legalEntity,
}: BrandsCardProps): JSX.Element {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const { data: brands = [], isLoading } = useQuery({
    queryKey: brandKeys.list(),
    queryFn: listBrands,
  });

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<Brand | null>(null);
  const [name, setName] = useState("");
  const [makeDefault, setMakeDefault] = useState(false);
  /**
   * The marca's adenda, split into the two states the column encodes. null on
   * the column means INHERIT the company's; "" means this marca deliberately
   * has none. A single string field cannot express both, which is why the
   * checkbox exists rather than relying on an empty textarea.
   */
  const [inheritAdenda, setInheritAdenda] = useState(true);
  const [adendaText, setAdendaText] = useState("");
  const [deleteTarget, setDeleteTarget] = useState<Brand | null>(null);
  /** Which row's colour is open: a marca, or the legal entity. */
  const [colorTarget, setColorTarget] = useState<Brand | "legal" | null>(null);

  // ---- Crop BEFORE upload, same rule as the tenant logo ----
  // Picking a file opens the crop editor against a local object URL; nothing
  // is sent until the brand is saved. Uploading first would commit an image
  // the user has not yet seen positioned.
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [pendingUrl, setPendingUrl] = useState<string | null>(null);
  const [pendingCrop, setPendingCrop] = useState<InvoiceLogoCropPayload | null>(null);
  const [cropOpen, setCropOpen] = useState(false);
  /** The brand whose STORED logo is being re-cropped from the row menu. */
  const [cropTarget, setCropTarget] = useState<Brand | null>(null);

  const clearPending = (): void => {
    setPendingUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev); // object URLs leak until revoked
      return null;
    });
    setPendingFile(null);
    setPendingCrop(null);
  };

  // Revoke on unmount too — navigating away mid-edit otherwise leaks.
  useEffect(() => {
    return () => {
      if (pendingUrl) URL.revokeObjectURL(pendingUrl);
    };
  }, [pendingUrl]);

  const invalidate = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: brandKeys.all });
  };

  const save = useMutation({
    mutationFn: async (): Promise<void> => {
      const trimmed = name.trim();
      // Send ONE of the two adenda keys, never both: useCompanyAdenda wins
      // server-side, and sending text alongside it would read as an edit that
      // was silently dropped.
      //
      // With NO company adenda there is no inherit/override choice on screen,
      // just a field — so an empty field must store NULL (inherit), never ""
      // (deliberately none). The two look identical today and diverge the
      // moment someone writes a company adenda: "" would refuse it forever.
      // "" stays reachable, but only where it is a real choice the user made:
      // picking "escribir una propia" and leaving it blank while the company
      // HAS one.
      const ownAdenda = hasCompanyAdenda
        ? !inheritAdenda
        : adendaText.trim().length > 0;
      const adendaPayload = ownAdenda
        ? { adendaText: adendaText.trim() }
        : { useCompanyAdenda: true };
      const brand = editing
        ? await updateBrand(editing.id, {
            name: trimmed,
            ...(makeDefault !== editing.isDefault ? { isDefault: makeDefault } : {}),
            ...adendaPayload,
          })
        : await createBrand({
            name: trimmed,
            isDefault: makeDefault,
            ...adendaPayload,
          });

      if (pendingFile) {
        // ORDER MATTERS: the upload resets the crop server-side, so saving the
        // crop first makes the feature silently do nothing.
        await uploadBrandLogo(brand.id, pendingFile);
        await updateBrand(brand.id, fromCropDialogPayload(pendingCrop ?? WHOLE_IMAGE_CROP));
      }
    },
    onSuccess: async () => {
      await invalidate();
      closeDialog();
      toast.success(t("brands.saved", "Marca guardada."));
    },
    onError: () => toast.error(t("brands.saveError", "No pudimos guardar la marca.")),
  });

  /** Re-crop of an already-stored brand logo, straight from the row menu. */
  const saveCrop = useMutation({
    mutationFn: async (payload: InvoiceLogoCropPayload): Promise<void> => {
      if (!cropTarget) return;
      await updateBrand(cropTarget.id, fromCropDialogPayload(payload));
    },
    onSuccess: async () => {
      await invalidate();
      setCropOpen(false);
      setCropTarget(null);
      toast.success(t("brands.logoAdjusted", "Logo ajustado."));
    },
    onError: () => toast.error(t("brands.saveError", "No pudimos guardar la marca.")),
  });

  const saveColor = useMutation({
    mutationFn: async ({
      brandId,
      brandColor,
    }: {
      brandId: string;
      brandColor: string | null;
    }): Promise<void> => {
      await updateBrand(brandId, { brandColor });
    },
    onSuccess: async () => {
      await invalidate();
      toast.success(t("brands.colorSaved", "Color actualizado."));
    },
    onError: () => toast.error(t("brands.saveError", "No pudimos guardar la marca.")),
  });

  const useTenantLogo = useMutation({
    mutationFn: (brandId: string) => updateBrand(brandId, { useSharedLogo: true }),
    onSuccess: async () => {
      await invalidate();
      toast.success(t("brands.usingCompanyLogo", "La marca usa el logo de la empresa."));
    },
    onError: () => toast.error(t("brands.saveError", "No pudimos guardar la marca.")),
  });

  const remove = useMutation({
    mutationFn: (brandId: string) => deleteBrand(brandId),
    onSuccess: async () => {
      await invalidate();
      setDeleteTarget(null);
      toast.success(t("brands.deleted", "Marca eliminada."));
    },
    onError: () => toast.error(t("brands.deleteError", "No pudimos eliminar la marca.")),
  });

  const closeDialog = (): void => {
    setDialogOpen(false);
    setEditing(null);
    setName("");
    setMakeDefault(false);
    setInheritAdenda(true);
    setAdendaText("");
    clearPending();
  };

  const openNew = (): void => {
    setEditing(null);
    setName("");
    setMakeDefault(brands.length === 0);
    // A new marca inherits by default — the common case, and the safe one: it
    // keeps whatever legend the company already prints.
    setInheritAdenda(true);
    setAdendaText("");
    clearPending();
    setDialogOpen(true);
  };

  /**
   * Set when the dialog was opened from the "Adenda de la marca" menu entry,
   * so it scrolls to the adenda instead of landing on the name field. The
   * adenda is the one control in here a user arrives looking for by name —
   * the others are reached by reading down the form.
   */
  const adendaBlockRef = useRef<HTMLDivElement | null>(null);
  /** Whether there is anything to inherit. Decides the whole control below. */
  const hasCompanyAdenda = Boolean(companyAdendaText?.trim());
  const [scrollToAdenda, setScrollToAdenda] = useState(false);
  useEffect(() => {
    if (!dialogOpen || !scrollToAdenda) return;
    // After the dialog's own mount animation, or the node is not laid out yet
    // and scrollIntoView is a no-op.
    const id = window.setTimeout(() => {
      adendaBlockRef.current?.scrollIntoView({ block: "center" });
      setScrollToAdenda(false);
    }, 120);
    return () => window.clearTimeout(id);
  }, [dialogOpen, scrollToAdenda]);

  const openEdit = (brand: Brand): void => {
    setEditing(brand);
    setName(brand.name);
    setMakeDefault(brand.isDefault);
    // null is inherit; "" and any text are this marca's own.
    setInheritAdenda(brand.adendaText === null);
    setAdendaText(brand.adendaText ?? "");
    clearPending();
    setDialogOpen(true);
  };

  const startCropForFile = (file: File | null): void => {
    if (!file) return;
    setPendingFile(file);
    setPendingUrl(URL.createObjectURL(file));
    setCropTarget(null);
    setCropOpen(true);
  };

  const openCropForStoredLogo = (brand: Brand): void => {
    setCropTarget(brand);
    setCropOpen(true);
  };

  // Memoised so the dialog is not re-seeded mid-drag by a parent re-render.
  const cropInitial = useMemo<InvoiceLogoCropPayload>(() => {
    if (cropTarget) return toCropDialogPayload(cropTarget);
    return pendingCrop ?? WHOLE_IMAGE_CROP;
  }, [cropTarget, pendingCrop]);

  const cropImageUrl = cropTarget
    ? cropTarget.logoImage ?? tenantInvoiceLogoUrl
    : pendingUrl;

  /**
   * Makes the legal entity the default by clearing isDefault from whichever
   * marca holds it. There is no "default = company" flag: no marca being
   * default IS the company being default, which is the same thing
   * invoice.brandId = null means server-side.
   */
  const clearDefault = useMutation({
    mutationFn: async (): Promise<void> => {
      const current = brands.find((b) => b.isDefault);
      if (!current) return;
      await updateBrand(current.id, { isDefault: false });
    },
    onSuccess: async () => {
      await invalidate();
      toast.success(t("brands.defaultSet", "Marca predeterminada actualizada."));
    },
    onError: () => toast.error(t("brands.saveError", "No pudimos guardar la marca.")),
  });

  const setDefault = useMutation({
    mutationFn: (brandId: string) => updateBrand(brandId, { isDefault: true }),
    onSuccess: async () => {
      await invalidate();
      toast.success(t("brands.defaultSet", "Marca predeterminada actualizada."));
    },
    onError: () => toast.error(t("brands.saveError", "No pudimos guardar la marca.")),
  });

  const rowActions = (brand: Brand): TableAction[] => {
    const actions: TableAction[] = [
      {
        label: t("brands.preview", "Vista previa de factura"),
        icon: Eye,
        onClick: () => void openPreview(brand.id),
        disabled: previewingId != null,
      },
      {
        label: t("common.edit", "Editar"),
        icon: Pencil,
        onClick: () => openEdit(brand),
      },
      {
        label: t("brands.adendaAction", "Adenda de la marca"),
        icon: FileText,
        onClick: () => {
          setScrollToAdenda(true);
          openEdit(brand);
        },
      },
      {
        label: t("brands.color", "Color de marca"),
        icon: Palette,
        onClick: () => setColorTarget(brand),
      },
    ];
    if (brand.logoImage) {
      actions.push(
        {
          label: t("brands.adjustLogo", "Ajustar logo"),
          icon: CropIcon,
          onClick: () => openCropForStoredLogo(brand),
        },
        {
          label: t("brands.useCompanyLogo", "Usar el logo de la empresa"),
          icon: Tags,
          onClick: () => useTenantLogo.mutate(brand.id),
          disabled: useTenantLogo.isPending,
        }
      );
    }
    if (!brand.isDefault) {
      actions.push({
        label: t("brands.makeDefault", "Usar como predeterminada"),
        icon: Star,
        onClick: () => setDefault.mutate(brand.id),
        disabled: setDefault.isPending,
      });
    }
    actions.push({
      label: t("common.delete", "Eliminar"),
      icon: Trash2,
      variant: "destructive",
      onClick: () => setDeleteTarget(brand),
      disabled: remove.isPending,
    });
    return actions;
  };

  /**
   * Renders the real invoice PDF for ONE marca, through the same endpoint and
   * the same resolver the company preview uses — so what this opens is what
   * the client receives, not an approximation drawn in the browser.
   */
  const [previewingId, setPreviewingId] = useState<string | null>(null);
  /** `null` previews the legal entity's own invoice — the same endpoint with
   *  no brandId, which is exactly what an unbranded CFE renders as. */
  const openPreview = async (brandId: string | null): Promise<void> => {
    setPreviewingId(brandId ?? LEGAL_ENTITY_ROW_ID);
    try {
      const { data } = await apiClient.get<Blob>(
        "/tenants/current/invoice-preview-pdf",
        {
          ...(brandId ? { params: { brandId } } : {}),
          responseType: "blob",
        }
      );
      const url = URL.createObjectURL(data);
      window.open(url, "_blank");
      // Revoked on a timer rather than immediately: the new tab still needs
      // the object URL to load the document.
      setTimeout(() => URL.revokeObjectURL(url), 30_000);
    } catch {
      toast.error(
        t("brands.previewError", "No pudimos generar la vista previa.")
      );
    } finally {
      setPreviewingId(null);
    }
  };

  const canSave = name.trim().length > 0 && !save.isPending;

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="text-base text-foreground">
            {t("brands.title", "Marcas")}
          </CardTitle>
          <CardDescription>
            {t(
              "brands.description",
              "Si facturás varios productos con la misma empresa, creá una marca por producto. Cambia el logo y el nombre que ve tu cliente en la factura; el RUT y la razón social siguen siendo los de la empresa."
            )}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {isLoading ? (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" aria-hidden />
            </div>
          ) : brands.length === 0 && !legalEntity ? (
            <p className="text-sm text-muted-foreground">
              {t(
                "brands.empty",
                "Todavía no creaste marcas. Tus facturas usan el nombre y el logo de la empresa."
              )}
            </p>
          ) : (
            <ul className="divide-y rounded-lg border">
              {legalEntity && (
                <li className="flex items-center gap-3 p-3">
                  <div
                    className="flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-md border bg-muted/30"
                    style={{ minWidth: PREVIEW_FRAME_PX }}
                  >
                    {tenantInvoiceLogoUrl ? (
                      <img
                        src={tenantInvoiceLogoUrl}
                        alt=""
                        className="max-h-full max-w-full object-contain"
                      />
                    ) : null}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="truncate text-sm font-medium text-foreground">
                        {legalEntity.name}
                      </span>
                      {/* Exactly one row in this list carries the badge, and
                          it answers the only question the list raises: what
                          does a new invoice use if I pick nothing? The legal
                          entity is that answer whenever no marca is default. */}
                      {!brands.some((b) => b.isDefault) ? (
                        <span className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-[11px] text-muted-foreground">
                          {t("brands.defaultBadge", "Predeterminada")}
                        </span>
                      ) : null}
                    </div>
                    <p className="truncate text-xs text-muted-foreground">
                      {/* Same line the marca rows show. The rows are the same
                          kind of thing, so they answer the same question. */}
                      {legalEntity.hasOwnLogo
                        ? t("brands.ownLogo", "Logo propio")
                        : t("brands.sharedLogo", "Usa el logo de la empresa")}
                    </p>
                  </div>
                  {/* Same affordance as the marca rows. A lone Editar button
                      here made this row read as a different kind of thing,
                      when the whole point of folding it in was that it is the
                      same kind of thing. */}
                  <TableActionsMenu
                    actions={[
                      {
                        label: t("brands.preview", "Vista previa de factura"),
                        icon: Eye,
                        onClick: () => void openPreview(null),
                        disabled: previewingId != null,
                      },
                      {
                        label: t("common.edit", "Editar"),
                        icon: Pencil,
                        onClick: legalEntity.onEdit,
                      },
                      {
                        label: t("brands.color", "Color de marca"),
                        icon: Palette,
                        onClick: () => setColorTarget("legal"),
                      },
                      ...(legalEntity.hasOwnLogo
                        ? [
                            {
                              label: t(
                                "brands.useCompanyLogo",
                                "Usar el logo de la empresa"
                              ),
                              icon: Tags,
                              onClick: legalEntity.onUseCompanyLogo,
                            },
                          ]
                        : []),
                      // Only when a marca currently holds the default —
                      // otherwise this row already IS the default and the
                      // action would be a no-op the badge contradicts.
                      ...(brands.some((b) => b.isDefault)
                        ? [
                            {
                              label: t(
                                "brands.makeDefault",
                                "Usar como predeterminada"
                              ),
                              icon: Star,
                              onClick: () => clearDefault.mutate(),
                              disabled: clearDefault.isPending,
                            },
                          ]
                        : []),
                    ]}
                  />
                </li>
              )}
              {brands.map((brand) => (
                <li key={brand.id} className="flex items-center gap-3 p-3">
                  <div
                    className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md border bg-muted/30"
                    style={{ minWidth: PREVIEW_FRAME_PX }}
                  >
                    <BrandLogoPreview
                      brand={brand}
                      fallbackLogoUrl={tenantInvoiceLogoUrl}
                      framePx={PREVIEW_FRAME_PX}
                    />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="truncate text-sm font-medium text-foreground">
                        {brand.name}
                      </span>
                      {brand.isDefault ? (
                        <span className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-[11px] text-muted-foreground">
                          {t("brands.defaultBadge", "Predeterminada")}
                        </span>
                      ) : null}
                    </div>
                    <p className="truncate text-xs text-muted-foreground">
                      {brand.logoImage
                        ? t("brands.ownLogo", "Logo propio")
                        : t("brands.sharedLogo", "Usa el logo de la empresa")}
                    </p>
                  </div>
                  {brand.brandColor ? (
                    <span
                      className="h-4 w-4 shrink-0 rounded-full border"
                      style={{ backgroundColor: brand.brandColor }}
                      aria-hidden
                    />
                  ) : null}
                  <TableActionsMenu actions={rowActions(brand)} />
                </li>
              ))}
            </ul>
          )}

          {/* Right-aligned action row, matching the card headers above where
              the action sits opposite the text. */}
          <div className="flex justify-end">
            <Button type="button" variant="outline" onClick={openNew}>
              <Plus className="mr-2 h-4 w-4" aria-hidden />
              {t("brands.new", "Agregar marca")}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Dialog
        open={dialogOpen}
        onOpenChange={(open) => {
          if (!open) closeDialog();
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editing
                ? t("brands.editTitle", "Editar marca")
                : t("brands.newTitle", "Nueva marca")}
            </DialogTitle>
            <DialogDescription>
              {t(
                "brands.dialogHelp",
                "El nombre y el logo aparecen en la factura PDF y en el mail al cliente."
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="brand-name">{t("brands.nameLabel", "Nombre comercial")}</Label>
              <Input
                id="brand-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={255}
                placeholder={t("brands.namePlaceholder", "ej. Fiscalo")}
              />
            </div>

            <FileUpload
              label={t("brands.logoLabel", "Logo de la marca")}
              helperText={t(
                "brands.logoHelper",
                "Solo JPG o PNG. Si no subís uno, la marca usa el logo de la empresa."
              )}
              acceptedMimeTypes={JPG_PNG_ACCEPT}
              currentImageUrl={pendingUrl ?? editing?.logoImage ?? tenantInvoiceLogoUrl}
              disabled={save.isPending}
              onFileChange={startCropForFile}
            />

            {/* ONE control per state, and no control that states a
                falsehood. This started as a lone ticked checkbox, then grew a
                panel, then a button inside the panel — three widgets for one
                binary choice, with "Usar la adenda de la empresa" sitting
                ticked for a company that had no adenda at all. The ticked
                state was simply not true.

                So: the inherit/override choice is offered ONLY when there is
                something to inherit. Otherwise it is just a field. */}
            <div className="space-y-2" ref={adendaBlockRef}>
              <Label>{t("brands.adendaSectionLabel", "Adenda")}</Label>

              {hasCompanyAdenda ? (
                <RadioGroup
                  value={inheritAdenda ? "company" : "own"}
                  onValueChange={(v) => setInheritAdenda(v === "company")}
                  disabled={save.isPending}
                  className="gap-3"
                >
                  <div className="space-y-2">
                    <label className="flex items-center gap-2 text-sm text-foreground">
                      <RadioGroupItem value="company" id="brand-adenda-company" />
                      {t("brands.useCompanyAdenda", "Usar la adenda de la empresa")}
                    </label>
                    {inheritAdenda && (
                      <p className="whitespace-pre-wrap rounded-md border border-[var(--fx-ink-100)] bg-[var(--fx-paper-2)] p-3 text-xs text-muted-foreground dark:border-[var(--dk-border)] dark:bg-[var(--dk-surface2)]">
                        {companyAdendaText}
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <label className="flex items-center gap-2 text-sm text-foreground">
                      <RadioGroupItem value="own" id="brand-adenda-own" />
                      {t("brands.ownAdendaOption", "Escribir una adenda propia")}
                    </label>
                    {!inheritAdenda && (
                      <AdendaField
                        id="brand-adenda"
                        value={adendaText}
                        onChange={setAdendaText}
                        disabled={save.isPending}
                        rows={3}
                        label=""
                        placeholder={t(
                          "brands.adendaPlaceholder",
                          "Texto que aparece solo en las facturas de esta marca."
                        )}
                        helper={t(
                          "brands.adendaHelperOverride",
                          "Reemplaza la adenda de la empresa en las facturas de esta marca. Vacío = esta marca no lleva adenda."
                        )}
                      />
                    )}
                  </div>
                </RadioGroup>
              ) : (
                /* Nothing to inherit, so no choice to offer — just the field.
                   Left empty it saves as NULL (inherit), not "" — see the
                   save mutation. */
                <AdendaField
                  id="brand-adenda"
                  value={adendaText}
                  onChange={setAdendaText}
                  disabled={save.isPending}
                  rows={3}
                  label=""
                  placeholder={t(
                    "brands.adendaPlaceholder",
                    "Texto que aparece solo en las facturas de esta marca."
                  )}
                  helper={t(
                    "brands.adendaHelperNoCompany",
                    "Se imprime al pie de las facturas de esta marca. Tu empresa no tiene adenda, así que dejarlo vacío significa que esta marca no lleva ninguna."
                  )}
                />
              )}
            </div>

            <label className="flex items-center gap-2 text-sm text-foreground">
              <Checkbox
                checked={makeDefault}
                onCheckedChange={(v) => setMakeDefault(v === true)}
                disabled={editing?.isDefault === true}
              />
              {t("brands.makeDefaultLabel", "Usar como marca predeterminada")}
            </label>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={closeDialog}>
              {t("common.cancel", "Cancelar")}
            </Button>
            <Button type="button" onClick={() => save.mutate()} disabled={!canSave}>
              {save.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
              ) : null}
              {t("common.save", "Guardar")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {cropOpen && cropImageUrl ? (
        <InvoiceLogoCropDialog
          open={cropOpen}
          onOpenChange={(open) => {
            if (!open) {
              setCropOpen(false);
              setCropTarget(null);
            }
          }}
          imageUrl={cropImageUrl}
          initial={cropInitial}
          saving={saveCrop.isPending}
          isNewUpload={cropTarget == null}
          onSave={(payload) => {
            if (cropTarget) {
              saveCrop.mutate(payload);
              return;
            }
            // New upload: hold the crop until the brand is saved, so cancelling
            // the whole dialog leaves nothing behind on the server.
            setPendingCrop(payload);
            setCropOpen(false);
          }}
        />
      ) : null}

      {colorTarget && (
        <BrandColorDialog
          open
          onOpenChange={(next) => {
            if (!next) setColorTarget(null);
          }}
          title={
            colorTarget === "legal" ? legalEntity?.name ?? "" : colorTarget.name
          }
          logoUrl={
            colorTarget === "legal"
              ? tenantInvoiceLogoUrl
              : colorTarget.logoImage ?? tenantInvoiceLogoUrl
          }
          savedColor={
            colorTarget === "legal"
              ? legalEntity?.brandColor ?? null
              : colorTarget.brandColor
          }
          saving={
            colorTarget === "legal"
              ? Boolean(legalEntity?.colorSaving)
              : saveColor.isPending
          }
          onSave={(color) => {
            if (colorTarget === "legal") {
              legalEntity?.onSaveColor(color);
            } else {
              saveColor.mutate({ brandId: colorTarget.id, brandColor: color });
            }
          }}
        />
      )}

      <ConfirmationDialog
        open={deleteTarget != null}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
        title={t("brands.deleteTitle", "Eliminar marca")}
        description={t(
          "brands.deleteDescription",
          "Las facturas ya emitidas con esta marca la siguen mostrando. Solo deja de aparecer al crear facturas nuevas."
        )}
        confirmText={t("common.delete", "Eliminar")}
        cancelText={t("common.cancel", "Cancelar")}
        variant="destructive"
        isLoading={remove.isPending}
        onConfirm={() => {
          if (deleteTarget) remove.mutate(deleteTarget.id);
        }}
      />
    </>
  );
}
